import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const PAGE_SIZE = 20;

const STATUS_COLORS = {
  APPROVED: theme.colors.success,
  REJECTED: theme.colors.error,
  PENDING: '#facc15',
  PAUSED: theme.colors.textMuted,
};

function Templates({ token }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [successMsg] = useState(
    location.state?.created
      ? 'Plantilla enviada a Meta. Estado: PENDING — se verificará en 5 minutos.'
      : ''
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function openDetail(id) {
    setDetailLoading(true);
    setDetail({ loading: true });
    fetch(`/api/templates/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(row => {
        if (!row) { setDetail(null); return; }
        // config puede llegar como objeto (columna JSON) o como string.
        let config = row.config;
        if (typeof config === 'string') {
          try { config = JSON.parse(config); } catch { config = null; }
        }
        const components = (config && config.components) || [];
        setDetail({ ...row, components });
      })
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }

  useEffect(() => {
    setLoading(true);
    fetch(`/api/templates?page=${page}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [], total: 0 })
      .then(d => { setData(d.data || []); setTotal(d.total || 0); })
      .finally(() => setLoading(false));
  }, [page, token]);

  return (
    <main style={{ padding: isMobile ? '1.25rem 1rem' : '2rem 1.5rem', maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        padding: isMobile ? '1.25rem' : '2rem',
        boxShadow: `0 4px 24px ${theme.colors.glowSubtle}`,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Plantillas</h1>
          <button
            onClick={() => navigate('/plantillas/nueva')}
            style={{
              padding: '9px 20px',
              backgroundColor: theme.colors.primary,
              color: theme.colors.background,
              border: 'none',
              borderRadius: '7px',
              fontSize: '0.9rem',
              fontWeight: '700',
              cursor: 'pointer',
            }}
          >
            + Crear
          </button>
        </div>

        {successMsg && (
          <div style={{
            backgroundColor: 'rgba(74,222,128,0.1)',
            border: `1px solid ${theme.colors.success}`,
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            color: theme.colors.success,
            fontSize: '0.88rem',
            marginBottom: '1.5rem',
          }}>
            {successMsg}
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.colors.textMuted, textAlign: 'center', padding: '2rem' }}>Cargando...</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
              <thead>
                <tr>
                  {['ID', 'Nombre', 'Idioma', 'Categoría', 'Estado', 'Creado', 'Acciones'].map(col => (
                    <th key={col} style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      borderBottom: `1px solid ${theme.colors.border}`,
                      color: theme.colors.primary,
                      fontWeight: '600',
                      whiteSpace: 'nowrap',
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: theme.colors.textMuted }}>
                      Sin plantillas. Crea la primera.
                    </td>
                  </tr>
                ) : data.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted }}>{row.id}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.text, fontWeight: '500' }}>{row.name}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.text }}>{row.language}</td>
                    <td style={{ padding: '9px 12px', color: theme.colors.text }}>
                      {row.category === 'UTILITY' ? 'Utilidad' : 'Marketing'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: '700',
                        backgroundColor: `${STATUS_COLORS[row.status] || theme.colors.textMuted}22`,
                        color: STATUS_COLORS[row.status] || theme.colors.textMuted,
                        border: `1px solid ${STATUS_COLORS[row.status] || theme.colors.textMuted}`,
                      }}>
                        {row.status}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px', color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>
                      {new Date(row.created_at).toLocaleDateString('es-CO')}
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <button
                        onClick={() => openDetail(row.id)}
                        style={{
                          padding: '5px 14px',
                          backgroundColor: 'transparent',
                          color: theme.colors.primary,
                          border: `1px solid ${theme.colors.primary}`,
                          borderRadius: '6px',
                          fontSize: '0.8rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                        }}
                      >
                        Ver
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.2rem' }}>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: `1px solid ${theme.colors.border}`,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              color: page === 1 ? theme.colors.textMuted : theme.colors.text,
              fontSize: '0.88rem',
            }}
          >← Anterior</button>
          <span style={{ color: theme.colors.textMuted, fontSize: '0.88rem' }}>
            Página {page} de {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: '6px 16px',
              borderRadius: '6px',
              border: `1px solid ${theme.colors.border}`,
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              backgroundColor: 'transparent',
              color: page === totalPages ? theme.colors.textMuted : theme.colors.text,
              fontSize: '0.88rem',
            }}
          >Siguiente →</button>
        </div>
      </div>

      {detail && (
        <TemplateDetailModal
          detail={detail}
          loading={detailLoading}
          isMobile={isMobile}
          onClose={() => setDetail(null)}
        />
      )}
    </main>
  );
}

// ---- Modal de detalle: muestra cómo se armó la plantilla ----

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ color: theme.colors.primary, fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ color: theme.colors.text, fontSize: '0.9rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  );
}

function MediaPreview({ format, url }) {
  if (!url) return <span style={{ color: theme.colors.textMuted }}>(sin archivo)</span>;
  if (format === 'IMAGE') {
    return <img src={url} alt="header" style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '8px', border: `1px solid ${theme.colors.border}` }} />;
  }
  if (format === 'VIDEO') {
    return <video src={url} controls style={{ maxWidth: '100%', maxHeight: '220px', borderRadius: '8px' }} />;
  }
  return <a href={url} target="_blank" rel="noreferrer" style={{ color: theme.colors.primary }}>Ver documento</a>;
}

function ButtonsView({ buttons }) {
  const labels = { QUICK_REPLY: 'Respuesta rápida', URL: 'Enlace', PHONE_NUMBER: 'Teléfono' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {(buttons || []).map((b, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', fontWeight: '700', color: theme.colors.textMuted, border: `1px solid ${theme.colors.border}`, borderRadius: '5px', padding: '1px 7px' }}>
            {labels[b.type] || b.type}
          </span>
          <span style={{ color: theme.colors.text, fontWeight: '600' }}>{b.text}</span>
          {b.url && <span style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>→ {b.url}</span>}
          {b.phone_number && <span style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>→ {b.phone_number}</span>}
        </div>
      ))}
    </div>
  );
}

function ComponentView({ comp }) {
  if (comp.type === 'BODY') return <Field label="Cuerpo">{comp.text}</Field>;
  if (comp.type === 'FOOTER') return <Field label="Pie de página">{comp.text}</Field>;
  if (comp.type === 'HEADER') {
    if (comp.format === 'TEXT') return <Field label="Encabezado (texto)">{comp.text}</Field>;
    const url = comp.example?.header_handle?.[0];
    return <Field label={`Encabezado (${comp.format})`}><MediaPreview format={comp.format} url={url} /></Field>;
  }
  if (comp.type === 'BUTTONS') return <Field label="Botones"><ButtonsView buttons={comp.buttons} /></Field>;
  if (comp.type === 'CAROUSEL') {
    return (
      <Field label={`Carrusel (${(comp.cards || []).length} tarjetas)`}>
        <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '6px' }}>
          {(comp.cards || []).map((card, i) => (
            <div key={i} style={{ minWidth: '230px', maxWidth: '230px', border: `1px solid ${theme.colors.border}`, borderRadius: '10px', padding: '10px', backgroundColor: theme.colors.background }}>
              <div style={{ color: theme.colors.textMuted, fontSize: '0.72rem', fontWeight: '700', marginBottom: '6px' }}>Tarjeta {i + 1}</div>
              {(card.components || []).map((cc, j) => <ComponentView key={j} comp={cc} />)}
            </div>
          ))}
        </div>
      </Field>
    );
  }
  return null;
}

function TemplateDetailModal({ detail, loading, isMobile, onClose }) {
  const components = detail.components || [];
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '1rem' : '2rem', zIndex: 1000,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: '14px',
          border: `1px solid ${theme.colors.border}`,
          padding: isMobile ? '1.25rem' : '1.75rem',
          maxWidth: '640px', width: '100%', maxHeight: '85vh', overflowY: 'auto',
          boxShadow: `0 8px 40px rgba(0,0,0,0.4)`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
          <h2 style={{ color: theme.colors.primary, fontSize: '1.15rem', margin: 0 }}>
            {loading || detail.loading ? 'Cargando…' : detail.name}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textMuted, fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}
          >×</button>
        </div>

        {loading || detail.loading ? (
          <p style={{ color: theme.colors.textMuted }}>Cargando detalle…</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap', marginBottom: '1.25rem', color: theme.colors.textMuted, fontSize: '0.82rem' }}>
              <span><b style={{ color: theme.colors.text }}>Idioma:</b> {detail.language}</span>
              <span><b style={{ color: theme.colors.text }}>Categoría:</b> {detail.category === 'UTILITY' ? 'Utilidad' : 'Marketing'}</span>
              <span><b style={{ color: theme.colors.text }}>Estado:</b> {detail.status}</span>
            </div>
            {components.length === 0 ? (
              <p style={{ color: theme.colors.textMuted }}>No hay configuración guardada para esta plantilla.</p>
            ) : (
              components.map((comp, i) => <ComponentView key={i} comp={comp} />)
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Templates;
